import { updateOrder } from "core/Services/CommonServices";
import { getOrderDetails, cancelOrder } from "core/Services/MerchantServices";
import { printPDFCard } from "core/Services/OperatorServices";
import { iOrderDetails } from "core/kangarooTypes/iOrderDetails";
import AssignDriverDialog from "features/Operator/components/AssignDriverDialog";
import jsPDF from "jspdf";
import {
  Download,
  LucideSave,
  MapPin,
  MessageCircle,
  Package,
  PlusCircle,
  StepBack,
  Truck,
  Box,
  Sparkles,
  XCircle,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "shared/Button";
import { formatDate, formatStatus } from "utils/DatesUtil";
import { getStatusColors } from "utils/statusColors";
import { useTranslation } from "react-i18next";
import { USER_ROLE } from "utils/constant";

const OrderDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const orderId = id?.split("=")[1];
  const [noteText, setNoteText] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [order, setOrder] = useState<iOrderDetails | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isStatusSubmitting, setIsStatusSubmitting] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [showCancelNote, setShowCancelNote] = useState(false);
  const [cancelNote, setCancelNote] = useState("");
  const [isPrinting, setIsPrinting] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const isOperator = location.search.includes("pageTitle=Operator");
  const userRole = localStorage.getItem(USER_ROLE);

  const fetchOrderDetails = async () => {
    try {
      setIsLoading(true);
      if (orderId) {
        const response = await getOrderDetails(orderId);
        setOrder(response.data);
        setSelectedStatus(response.data.status);
      }
    } catch (error) {
      toast.error("Failed to load order details.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await updateOrder({ note: noteText }, orderId!);
      setNoteText("");
      order?.notes.push({
        id: order.notes.length + 1,
        content: noteText,
        created_by: "You",
        created_at: new Date().toISOString(),
      });
      toast.success("Note added successfully.");
    } catch (error: any) {
      toast.error(error.response.data.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToOrders = () => {
    const userRole = localStorage.getItem(USER_ROLE);

    // Check if there's a merchant_id in the URL search params (passed when navigating from merchant dashboard)
    const urlParams = new URLSearchParams(location.search);
    const merchantIdFromUrl = urlParams.get("merchantId");

    // Get merchant_id from the order object or URL
    const merchantId = merchantIdFromUrl || order?.merchant_id?.toString();

    // If admin/operator and we have merchantId, go back to that merchant's dashboard
    if ((userRole === "admin" || userRole === "operator") && merchantId) {
      navigate(`/admin-merchant-dashboard/${merchantId}`);
    }
    // If merchant viewing their own orders
    else if (userRole === "merchant") {
      navigate("/merchant-dashboard");
    }
    // If operator viewing driver's orders
    else if (order?.driver?.id && userRole === "operator") {
      navigate(`/merchant-dashboard/${order.driver.id}`);
    }
    // Fallback to orders list
    else {
      navigate("/merchant-dashboard");
    }
  };

  const handleStatusChange = async () => {
    if (selectedStatus === "canceled" && !showCancelNote) {
      setShowCancelNote(true);
      return;
    }

    if (selectedStatus === "canceled" && !cancelNote.trim()) {
      toast.error("Please provide a reason for cancellation");
      return;
    }

    try {
      setIsStatusSubmitting(true);
      const updateData = {
        status: selectedStatus,
        ...(selectedStatus === "canceled" && { note: cancelNote }),
      };
      await updateOrder(updateData, orderId!);
      if (order) {
        setOrder({
          ...order,
          status: selectedStatus,
          timeline: [
            ...order.timeline,
            {
              action: `Status changed to ${selectedStatus.replace("_", " ")}`,
              created_at: new Date().toISOString(),
              id: order.timeline.length + 1,
            },
          ],
          ...(selectedStatus === "canceled" && {
            notes: [
              ...order.notes,
              {
                id: order.notes.length + 1,
                content: cancelNote,
                created_by: "You",
                created_at: new Date().toISOString(),
              },
            ],
          }),
        });
      }

      toast.success(`Order status changed to ${selectedStatus}`);
      setShowCancelNote(false);
      setCancelNote("");
    } catch (error: any) {
      toast.error(error.response.data.message || "Failed to update status");
    } finally {
      setIsStatusSubmitting(false);
    }
  };

  const handlePrintOrder = async () => {
    if (!orderId) return;
    try {
      setIsPrinting(true);
      await printPDFCard(Number(orderId), `order_card_${orderId}`);
    } catch (error) {
      toast.error("Failed to print order card");
    } finally {
      setIsPrinting(false);
    }
  };

  const getStatusColorClasses = (status?: string) => {
    if (!status) return "bg-gray-100 text-gray-800";
    const colors = getStatusColors(status);
    return `${colors.bg} ${colors.text}`;
  };

  // Fulfillment workflow statuses
  const statusOptions = [
    {
      value: "ready_for_packaging",
      label: t("orderDetails.statusOptions.readyForPackaging"),
    },
    { value: "on_hold", label: t("orderDetails.statusOptions.onHold") },
    {
      value: "ready_for_shipping",
      label: t("orderDetails.statusOptions.readyForShipping"),
    },
    { value: "shipped", label: t("orderDetails.statusOptions.shipped") },
    {
      value: "ship_partially",
      label: t("orderDetails.statusOptions.shipPartially"),
    },
    { value: "handed_off", label: t("orderDetails.statusOptions.handedOff") },
    { value: "canceled", label: t("orderDetails.statusOptions.canceled") },
  ];

  const handleDriverAssignmentComplete = () => {
    setIsAssignDialogOpen(false);
    fetchOrderDetails();
  };

  const handleCancelOrder = async () => {
    if (!orderId) return;
    try {
      setIsCanceling(true);
      await cancelOrder(Number(orderId));
      toast.success(
        t("orderDetails.orderCanceledSuccess") || "Order canceled successfully"
      );
      setShowCancelDialog(false);
      fetchOrderDetails();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          t("orderDetails.orderCancelFailed") ||
          "Failed to cancel order"
      );
    } finally {
      setIsCanceling(false);
    }
  };

  // Can cancel if order is not already canceled or delivered
  const canCancelOrder =
    order?.status &&
    !["canceled", "delivered", "handed_off"].includes(order.status);

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <div className="flex justify-between flex-col md:flex-row items-center  gap-4">
        <Button
          onClick={handleBackToOrders}
          icon={<StepBack size={20} />}
          className="w-full sm:w-auto whitespace-nowrap"
        >
          {t("orderDetails.backToOrders")}
        </Button>

        {/* Fulfillment Order Workflow Actions */}
        {order?.type === "fulfillment" && (
          <div className="flex gap-2 flex-wrap w-full">
            {/* Packaging Step - available for ready_for_packaging or on_hold */}
            {userRole !== "merchant" &&
              (order.status === "ready_for_packaging" ||
                order.status === "on_hold") && (
                <Button
                  onClick={() => {
                    const urlParams = new URLSearchParams(location.search);
                    const merchantId =
                      urlParams.get("merchantId") ||
                      order?.merchant_id?.toString();
                    const packagingUrl = `/order-packaging/${orderId}${
                      merchantId ? `?merchantId=${merchantId}` : ""
                    }`;
                    navigate(packagingUrl);
                  }}
                  icon={<Package size={20} />}
                  className="w-full sm:w-auto"
                >
                  {t("orderDetails.proceedToPackaging") ||
                    "Proceed to Packaging"}
                </Button>
              )}

            {/* Delivery Method Step - available for ready_for_shipping */}
            {userRole !== "merchant" &&
              order.status === "ready_for_shipping" && (
                <Button
                  onClick={() => {
                    const urlParams = new URLSearchParams(location.search);
                    const merchantId =
                      urlParams.get("merchantId") ||
                      order?.merchant_id?.toString();
                    const deliveryUrl = `/order-delivery/${orderId}${
                      merchantId ? `?merchantId=${merchantId}` : ""
                    }`;
                    navigate(deliveryUrl);
                  }}
                  icon={<Truck size={20} />}
                  className="w-full sm:w-auto"
                >
                  {t("orderDetails.setDeliveryMethod") || "Set Delivery Method"}
                </Button>
              )}

            <Button
              onClick={handlePrintOrder}
              icon={<Download size={20} />}
              variant="outlined"
              className="w-full sm:w-auto"
              isLoading={isPrinting}
            >
              {t("orderDetails.printOrderCard")}
            </Button>

            {/* Cancel Order Button */}
            {canCancelOrder && (
              <Button
                onClick={() => setShowCancelDialog(true)}
                icon={<XCircle size={20} />}
                variant="outlined"
                className="w-full sm:w-auto text-red-600 border-red-600 hover:bg-red-50"
              >
                {t("orderDetails.cancelOrder") || "Cancel Order"}
              </Button>
            )}
          </div>
        )}

        {/* Delivery Order Actions (existing) */}
        {isOperator && order?.type !== "fulfillment" && (
          <div className="flex gap-2 flex-wrap w-full">
            <Button
              onClick={handlePrintOrder}
              icon={<Download size={20} />}
              variant="outlined"
              className="w-full sm:w-auto"
              isLoading={isPrinting}
            >
              {t("orderDetails.printOrderCard")}
            </Button>

            {order?.driver && (
              <Button
                onClick={() => setIsAssignDialogOpen(true)}
                icon={<PlusCircle size={20} />}
                variant="outlined"
                className="w-full sm:w-auto"
              >
                {t("orderDetails.reassignDriver")}
              </Button>
            )}

            {!order?.driver && (
              <Button
                onClick={() => setIsAssignDialogOpen(true)}
                icon={<PlusCircle size={20} />}
                className="w-full sm:w-auto"
              >
                {t("orderDetails.assignDriver")}
              </Button>
            )}
          </div>
        )}
      </div>

      <div ref={printRef} className="grid md:grid-cols-3 gap-6">
        {/* Left Column - Main Details */}
        <div className="md:col-span-2 space-y-6">
          {/* Order Header */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  {t("orderDetails.orderId")}
                </span>
                <span className="font-medium">{orderId}</span>
              </div>
              {isOperator ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    className="border border-gray-300 rounded-md p-2 "
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    <option value="">{t("common.status")}</option>
                    {statusOptions?.map((Status) => (
                      <option key={Status.value} value={Status.value}>
                        {Status.label}
                      </option>
                    ))}
                  </select>
                  <Button
                    onClick={handleStatusChange}
                    isLoading={isStatusSubmitting}
                    disabled={isLoading || selectedStatus === order?.status}
                  >
                    {t("common.update")}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  {isLoading ? (
                    <Skeleton width={80} />
                  ) : (
                    <span
                      className={`px-2 py-1 rounded-full text-xs flex items-center justify-center ${getStatusColorClasses(
                        order?.status
                      )}`}
                    >
                      {formatStatus(order?.status!)}
                    </span>
                  )}
                </div>
              )}
              <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                {isLoading ? (
                  <Skeleton width={80} />
                ) : order?.type ? (
                  order.type.charAt(0).toUpperCase() + order.type.slice(1)
                ) : (
                  "N/A"
                )}
              </span>
            </div>
            <div className="mt-4 space-y-2">
              {isLoading ? (
                <Skeleton width={200} height={20} />
              ) : order?.size ? (
                <>
                  <p className="text-sm text-gray-600">
                    {t("orderDetails.packageSize")}: {order.size.name}
                  </p>
                  <p className="text-sm text-gray-600">
                    {t("orderDetails.description")}: {order.size.description}
                  </p>
                  <div className="flex gap-4">
                    <p className="text-sm text-gray-600">
                      {t("orderDetails.price")}: JOD {order.size.price}
                    </p>
                    {order.size.old_price && (
                      <p className="text-sm text-gray-500 line-through">
                        {t("orderDetails.priceBeforeDiscount")}: JOD{" "}
                        {order.size.old_price}
                      </p>
                    )}
                    {(order.size.discount ?? 0) > 0 && (
                      <p className="text-sm text-green-600">
                        {t("orderDetails.discount")}: {order.size.discount}%
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  {t("orderDetails.noPackageSizeInfo") ||
                    "No package size information"}
                </p>
              )}
            </div>
          </div>
          {/* Merchant Information */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <span className="font-medium block mb-3">
              {t("orderDetails.merchantInformation")}
            </span>
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div className="flex flex-col gap-2">
                <span className="font-medium">
                  {isLoading ? (
                    <Skeleton width={120} />
                  ) : (
                    `${t("orderDetails.name")}: ${
                      order?.merchant?.first_name
                    } ${order?.merchant?.last_name || ""}` || "N/A"
                  )}
                </span>
                <span className="text-gray-600">
                  {isLoading ? (
                    <Skeleton width={120} />
                  ) : (
                    `${t("orderDetails.phone")}: ${
                      order?.merchant?.phone_number || "N/A"
                    }`
                  )}
                </span>
                <Button
                  icon={<MessageCircle size={16} />}
                  variant="outlined"
                  className="h-fit w-full sm:w-auto"
                  disabled={isLoading}
                  onClick={() =>
                    window.open(
                      `https://wa.me/${order?.merchant?.phone_number}`
                    )
                  }
                >
                  {t("orderDetails.whatsapp")}
                </Button>
              </div>
            </div>
          </div>

          {/* Driver Information */}
          {order?.driver?.first_name && isOperator && (
            <div className="bg-white rounded-lg shadow-md p-4">
              <span className="font-medium block mb-3">
                {t("orderDetails.driverInformation")}
              </span>
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex flex-col gap-2">
                  <span className="font-medium">
                    {`${t("orderDetails.name")}: ${order.driver.first_name} ${
                      order.driver.last_name || ""
                    }`}
                  </span>
                  <span className="text-gray-600">
                    {`${t("orderDetails.phone")}: ${
                      order.driver.phone_number || "N/A"
                    }`}
                  </span>
                  <Button
                    icon={<MessageCircle size={16} />}
                    variant="outlined"
                    className="h-fit w-full sm:w-auto"
                    onClick={() =>
                      window.open(`https://wa.me/${order.driver.phone_number}`)
                    }
                  >
                    {t("orderDetails.whatsapp")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <span className="font-medium block mb-3">
              {t("orderDetails.customerInformation")}
            </span>
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
              <div className="flex flex-col gap-2">
                <span className="font-medium">
                  {isLoading ? (
                    <Skeleton width={100} />
                  ) : (
                    `${t("orderDetails.name")}: ${
                      order?.customer?.name || "N/A"
                    }`
                  )}
                </span>
                <span className="text-gray-600">
                  {isLoading ? (
                    <Skeleton width={120} />
                  ) : (
                    `${t("orderDetails.phone")}: ${
                      order?.customer?.phone || "N/A"
                    }`
                  )}
                </span>
                <Button
                  icon={<MessageCircle size={16} />}
                  variant="outlined"
                  className="w-full sm:w-auto"
                  disabled={isLoading}
                  onClick={() =>
                    window.open(`https://wa.me/${order?.customer?.phone}`)
                  }
                >
                  {t("orderDetails.whatsapp")}
                </Button>
              </div>
              <div className="flex flex-col gap-2 sm:items-end">
                <span className="text-gray-600">
                  {t("orderDetails.city")}:{" "}
                  {isLoading ? (
                    <Skeleton width={80} />
                  ) : (
                    order?.customer?.city || "N/A"
                  )}
                </span>
                <span className="text-gray-600">
                  {t("orderDetails.area")}:{" "}
                  {isLoading ? (
                    <Skeleton width={80} />
                  ) : (
                    order?.customer?.area || "N/A"
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <span className="font-medium block mb-3">
              {t("orderDetails.orderItems")}
            </span>

            {isLoading ? (
              <Skeleton count={3} height={40} />
            ) : order?.type === "fulfillment" ? (
              // Fulfillment order - Show detailed product information
              <div className="space-y-3">
                {order.items.map((item, index) => (
                  <div
                    key={index}
                    className="border rounded-lg p-4 bg-gradient-to-r from-blue-50 to-white"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-semibold text-base text-gray-800">
                              {item.product?.product_name || item.name}
                            </h4>
                            {item.product?.sku && (
                              <p className="text-sm text-gray-500 mt-1">
                                SKU:{" "}
                                <span className="font-mono">
                                  {item.product.sku}
                                </span>
                              </p>
                            )}
                          </div>
                          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                            Qty: {item.quantity}
                          </span>
                        </div>

                        {item.product && (
                          <div className="flex flex-col sm:flex-row sm:gap-6 gap-2 mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600">
                                Unit Price:
                              </span>
                              <span className="text-sm font-semibold text-gray-800">
                                JOD {item.product.unit_price.toFixed(2)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600">
                                Subtotal:
                              </span>
                              <span className="text-base font-bold text-blue-600">
                                JOD {item.product.subtotal.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Delivery order - Show simple list
              <>
                <div className="border-b-4 pb-2 mb-2 flex justify-between text-sm text-gray-600">
                  <span className="font-semibold text-base">
                    {t("orderDetails.itemName")}
                  </span>
                  <span className="font-semibold text-base">
                    {t("orderDetails.quantity")}
                  </span>
                </div>
                {order?.items.map((item, index) => (
                  <div
                    key={index}
                    className="flex justify-between text-sm text-gray-600 border-b pb-2 mb-2"
                  >
                    <span className="font-semibold text-base">{item.name}</span>
                    <span>{item.quantity}</span>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Packaging & Services Section - Only for fulfillment orders */}
          {order?.type === "fulfillment" && (
            <div className="bg-white rounded-lg shadow-md p-4">
              <span className="font-medium block mb-4">
                {t("orderDetails.packagingAndServices") ||
                  "Packaging & Services"}
              </span>

              {isLoading ? (
                <Skeleton count={3} height={30} />
              ) : (
                <div className="space-y-4">
                  {/* Pick & Pack Fee */}
                  {order.pick_pack_fee !== undefined &&
                    order.pick_pack_fee > 0 && (
                      <div className="border rounded-lg p-3 bg-blue-50">
                        <div className="flex items-center gap-2 mb-2">
                          <Package size={18} className="text-blue-600" />
                          <span className="font-semibold text-blue-800">
                            {t("orderDetails.pickPackFee") || "Pick & Pack Fee"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">
                            {t("orderDetails.basedOnItemCount") ||
                              "Based on item count"}
                          </span>
                          <span className="font-bold text-blue-600">
                            JOD {Number(order.pick_pack_fee).toFixed(3)}
                          </span>
                        </div>
                      </div>
                    )}

                  {/* Item-Level Services */}
                  {order.items.some(
                    (item) =>
                      item.item_services && item.item_services.length > 0
                  ) && (
                    <div className="border rounded-lg p-3 bg-purple-50">
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles size={18} className="text-purple-600" />
                        <span className="font-semibold text-purple-800">
                          {t("orderDetails.itemServices") || "Item Services"}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {order.items.map((item, itemIndex) =>
                          item.item_services &&
                          item.item_services.length > 0 ? (
                            <div
                              key={itemIndex}
                              className="bg-white rounded p-2"
                            >
                              <p className="text-sm font-medium text-gray-700 mb-1">
                                {item.product?.product_name || item.name} (x
                                {item.quantity})
                              </p>
                              <div className="ml-3 space-y-1">
                                {item.item_services.map(
                                  (service, serviceIndex) => (
                                    <div
                                      key={serviceIndex}
                                      className="flex justify-between text-sm"
                                    >
                                      <span className="text-gray-600">
                                        {service.name}
                                        {service.quantity &&
                                          service.quantity > 1 && (
                                            <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                                              x{service.quantity}
                                            </span>
                                          )}
                                      </span>
                                      <span className="text-purple-600 font-medium">
                                        JOD{" "}
                                        {Number(
                                          service.price *
                                            (service.quantity || 1)
                                        ).toFixed(3)}
                                      </span>
                                    </div>
                                  )
                                )}
                              </div>
                              {item.services_fee !== undefined &&
                                item.services_fee > 0 && (
                                  <div className="flex justify-between text-sm mt-1 pt-1 border-t">
                                    <span className="text-gray-600 font-medium">
                                      {t("orderDetails.itemServiceTotal") ||
                                        "Item Service Total"}
                                    </span>
                                    <span className="text-purple-700 font-bold">
                                      JOD {Number(item.services_fee).toFixed(3)}
                                    </span>
                                  </div>
                                )}
                            </div>
                          ) : null
                        )}
                      </div>
                    </div>
                  )}

                  {/* Order-Level Services */}
                  {order.order_services && order.order_services.length > 0 && (
                    <div className="border rounded-lg p-3 bg-green-50">
                      <div className="flex items-center gap-2 mb-3">
                        <Box size={18} className="text-green-600" />
                        <span className="font-semibold text-green-800">
                          {t("orderDetails.orderServices") || "Order Services"}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {order.order_services.map((service, index) => (
                          <div
                            key={index}
                            className="flex justify-between text-sm bg-white rounded p-2"
                          >
                            <span className="text-gray-700">
                              {service.name}
                            </span>
                            <span className="text-green-600 font-medium">
                              JOD {Number(service.price).toFixed(3)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Services Summary */}
                  {order.services_fee !== undefined &&
                    order.services_fee > 0 && (
                      <div className="border-t pt-3 mt-3">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-700">
                            {t("orderDetails.totalServicesFee") ||
                              "Total Services Fee"}
                          </span>
                          <span className="font-bold text-lg text-gray-800">
                            JOD {Number(order.services_fee).toFixed(3)}
                          </span>
                        </div>
                      </div>
                    )}

                  {/* No Services Message */}
                  {(!order.pick_pack_fee || order.pick_pack_fee === 0) &&
                    (!order.order_services ||
                      order.order_services.length === 0) &&
                    !order.items.some(
                      (item) =>
                        item.item_services && item.item_services.length > 0
                    ) && (
                      <div className="text-center text-gray-500 py-4">
                        <Package
                          size={32}
                          className="mx-auto mb-2 text-gray-300"
                        />
                        <p>
                          {t("orderDetails.noServicesSelected") ||
                            "No packaging services selected yet"}
                        </p>
                      </div>
                    )}
                </div>
              )}
            </div>
          )}

          {/* Payment Information */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <span className="font-medium block mb-3">
              {isLoading ? (
                <Skeleton width={150} />
              ) : (
                t("orderDetails.paymentInformation")
              )}
            </span>

            <div className="flex items-center mb-4">
              <div className="w-3 h-3 bg-gray-300 rounded-full me-2"></div>
              {isLoading ? (
                <Skeleton width={100} />
              ) : (
                <span>{order?.payment_method}</span>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {t("orderDetails.orderAmount")}
                </span>
                {isLoading ? (
                  <Skeleton width={50} />
                ) : (
                  <span className="font-medium">JOD {order?.price}</span>
                )}
              </div>
              {/* Pick & Pack Fee - for fulfillment orders */}
              {order?.type === "fulfillment" &&
                order.pick_pack_fee !== undefined &&
                order.pick_pack_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {t("orderDetails.pickPackFee") || "Pick & Pack Fee"}
                    </span>
                    <span className="font-medium">
                      JOD {Number(order.pick_pack_fee).toFixed(3)}
                    </span>
                  </div>
                )}
              {/* Services Fee - for fulfillment orders */}
              {order?.type === "fulfillment" &&
                order.services_fee !== undefined &&
                order.services_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {t("orderDetails.servicesFee") || "Services Fee"}
                    </span>
                    <span className="font-medium">
                      JOD {Number(order.services_fee).toFixed(3)}
                    </span>
                  </div>
                )}
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {t("orderDetails.deliveryFee")}
                </span>
                {isLoading ? (
                  <Skeleton width={50} />
                ) : (
                  <span className="font-medium">
                    JOD {order?.delevery_fees || 0}
                  </span>
                )}
              </div>
              {/* Grand Total for fulfillment orders */}
              {order?.type === "fulfillment" &&
                order.grand_total !== undefined && (
                  <div className="flex justify-between pt-2 border-t mt-2">
                    <span className="font-semibold text-gray-800">
                      {t("orderDetails.grandTotal") || "Grand Total"}
                    </span>
                    <span className="font-bold text-lg text-blue-600">
                      JOD {Number(order.grand_total).toFixed(3)}
                    </span>
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Right Column - Timeline and Notes */}
        <div className="md:col-span-1 space-y-6">
          {/* Order Timeline */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <span className="font-medium block mb-4">
              {t("orderDetails.orderTimeline")}
            </span>
            <div className="relative max-h-64 overflow-y-auto">
              {isLoading ? (
                <Skeleton count={3} height={30} />
              ) : (
                order?.timeline?.map((event, index) => (
                  <div key={index} className="relative pl-8 pb-5">
                    <div className="absolute left-0 top-1 w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-sm font-medium">{event.action}</span>
                    <span className="text-xs text-gray-500 block">
                      {formatDate(event.created_at)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-lg shadow-md p-4 flex flex-col gap-2">
            <span className="font-medium block">{t("orderDetails.notes")}</span>
            <textarea
              className="w-full border rounded-md p-2 text-sm"
              placeholder={t("orderDetails.addNote")}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <div className="flex justify-end">
              <Button
                className="w-fit"
                icon={<LucideSave size={16} />}
                variant="outlined"
                onClick={handleAddNote}
                isLoading={isSubmitting}
                disabled={!noteText.trim()}
              >
                {t("common.submit")}
              </Button>
            </div>
            <div className="bg-gray-50 p-3 rounded-md">
              {isLoading ? (
                <Skeleton height={40} />
              ) : (
                order?.notes
                  .sort(
                    (a, b) =>
                      new Date(b.created_at).getTime() -
                      new Date(a.created_at).getTime()
                  )
                  .map((note, index) => (
                    <div key={index} className="mb-2">
                      <span className="text-sm font-medium">
                        {note.content}
                      </span>
                      <span className="text-xs text-gray-500 block">
                        Added By : {note.created_by} -{" "}
                        {formatDate(note.created_at)}
                      </span>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>
      </div>

      {showCancelNote && (
        <div className="fixed inset-[-2rem] bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg w-96">
            <h3 className="text-lg font-medium mb-4">
              {t("orderDetails.cancellationReason")}
            </h3>
            <textarea
              className="w-full border rounded-md p-2 mb-4"
              placeholder={t("orderDetails.provideCancellationReason")}
              value={cancelNote}
              onChange={(e) => setCancelNote(e.target.value)}
              rows={4}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outlined"
                onClick={() => {
                  setShowCancelNote(false);
                  setSelectedStatus(order?.status || "");
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleStatusChange}
                isLoading={isStatusSubmitting}
                disabled={!cancelNote.trim()}
              >
                {t("common.submit")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Driver Assignment Dialog */}
      <AssignDriverDialog
        isOpen={isAssignDialogOpen}
        onClose={() => setIsAssignDialogOpen(false)}
        order={order as any}
        onAssignmentComplete={handleDriverAssignmentComplete}
      />

      {/* Cancel Order Confirmation Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-96 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold">
                {t("orderDetails.cancelOrderTitle") || "Cancel Order"}
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              {t("orderDetails.cancelOrderConfirmation") ||
                "Are you sure you want to cancel this order? This action cannot be undone."}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outlined"
                onClick={() => setShowCancelDialog(false)}
                disabled={isCanceling}
              >
                {t("common.no") || "No"}
              </Button>
              <Button
                onClick={handleCancelOrder}
                isLoading={isCanceling}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {t("orderDetails.yesCancelOrder") || "Yes, Cancel Order"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetails;
